import { useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, Sparkles } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { ReportOutline } from '@/components/ui/report-outline';
import { StatusChip } from '@/components/ui/status-chip';
import { Callout, WorkspaceButton } from '@/components/ui/workspace';
import { PdfPreviewModal } from '@/components/modals/pdf-preview-modal';
import { SectionEditor } from '@/components/health/synthesis/section-editor';
import { useEngagementQuery } from '@/lib/api/engagements';
import {
  GO_LIVE_SECTION_DESCRIPTION,
  useCanLeadHealthReadiness,
  useDraftReportMutation,
  useEditReportSectionMutation,
  useHealthReadinessReportQuery,
  usePublishReportMutation,
  type HealthReadinessReport,
  type ReportCheck,
} from '@/lib/api/health-readiness-report';
import { formatDate } from '@/lib/utils/format';
import { ReportCover } from './report-cover';
import { ReportText } from './report-text';

/**
 * Health & Readiness → Report (E20 design §3; T-0245 / E22 design §5;
 * prototype `reportTab()`): an outline of the sections on the left (each
 * marked AI draft or edited; a click scrolls to it) and the document on the
 * right — the cover with its three figures, then the six health sections
 * and the go-live readiness section, each in `SectionEditor` with its
 * provenance chip.
 *
 * One Redraft with AI (sections a consultant edited keep their text), one
 * Publish that freezes one snapshot and renders one PDF. The figures and
 * every number in the text are the rollups from the API — live on a draft,
 * frozen on a published report: the text carries `{{…}}` placeholders the
 * read fills, and the checks it returns (a percentage typed in the prose, a
 * placeholder no rollup answers) are shown before Publish without blocking
 * it. The AI writes the words only.
 */

/** Why Publish is not available yet, in the words the API would use; null when it is. */
export function publishBlocker(report: HealthReadinessReport | null): string | null {
  if (!report) return 'Draft the report first.';
  if (report.status !== 'draft') return 'This report is published.';
  if (report.sections.length === 0) return 'The draft has no sections yet; redraft it.';
  const empty = report.sections.find((s) => s.content_text.trim().length === 0);
  if (empty) return `"${empty.title}" is empty; edit it before publishing.`;
  const refusal = report.sections.find((s) => s.is_safe_refusal);
  if (refusal) return `"${refusal.title}" has no AI draft; write it before publishing.`;
  if (report.go_live_section === 'missing') {
    return 'The go-live readiness section is not in this draft; redraft to add it.';
  }
  return null;
}

const sectionAnchor = (sectionId: string) => `report-sec-${sectionId}`;

function jumpTo(sectionId: string) {
  document.getElementById(sectionAnchor(sectionId))?.scrollIntoView?.({ behavior: 'smooth' });
}

const EDIT_HINT =
  'Keep the {{…}} placeholders: the report fills them from the rollups. Do not type percentages.';

export function ReportPane({ engagementId, today }: { engagementId: string; today?: Date }) {
  const canLead = useCanLeadHealthReadiness();
  const query = useHealthReadinessReportQuery(engagementId);
  const engagement = useEngagementQuery(engagementId);
  const draft = useDraftReportMutation(engagementId);
  const edit = useEditReportSectionMutation(engagementId);
  const publish = usePublishReportMutation(engagementId);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!query.data) {
    return (
      <div className="flex flex-col gap-4" data-testid="report-pane">
        {query.error ? (
          <ErrorMessage error={query.error} />
        ) : (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading the report…
          </div>
        )}
      </div>
    );
  }

  const { report, verdict, modules } = query.data;
  const figures = query.data.figures ?? null;
  const placeholders = query.data.placeholders ?? {};
  const checks = query.data.checks ?? [];
  const isDraft = report?.status === 'draft';
  const blocker = publishBlocker(report);
  const busy = draft.isPending || publish.isPending;
  const title = engagement.data ? `${engagement.data.client_name} · ${engagement.data.name}` : null;

  const runDraft = () => {
    setNotice(null);
    setConfirmPublish(false);
    const hadDraft = isDraft;
    draft.mutate(
      {},
      {
        onSuccess: () =>
          setNotice(
            hadDraft
              ? 'Redrafted the AI sections; the sections you edited are kept.'
              : 'Drafted. Edit any section before you publish.',
          ),
      },
    );
  };

  const runPublish = () => {
    setNotice(null);
    publish.mutate(undefined, {
      onSuccess: () => {
        setConfirmPublish(false);
        setNotice('Published. One PDF, with the figures frozen as of today.');
      },
    });
  };

  const draftLabel = !report
    ? 'Draft with AI'
    : isDraft
      ? 'Redraft with AI'
      : 'Start the next draft';
  const openPdf =
    report && !isDraft && report.snapshot_id ? (
      <WorkspaceButton variant="primary" onClick={() => setPdfOpen(true)}>
        <FileText className="size-3.5" aria-hidden />
        Open PDF
      </WorkspaceButton>
    ) : null;
  const actions =
    canLead && modules.health_check ? (
      <>
        <WorkspaceButton onClick={runDraft} disabled={busy}>
          {draft.isPending ? <Spinner /> : <Sparkles className="size-3.5" aria-hidden />}
          {draft.isPending ? 'Drafting…' : draftLabel}
        </WorkspaceButton>
        {openPdf ?? (
          <WorkspaceButton
            variant="primary"
            disabled={blocker !== null || busy}
            title={blocker ?? undefined}
            onClick={() => setConfirmPublish(true)}
          >
            Publish &amp; PDF
          </WorkspaceButton>
        )}
      </>
    ) : (
      openPdf
    );

  const head = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {report ? <ReportStatusChip status={report.status} /> : null}
        <span className="t-meta">
          {report ? <ReportMeta report={report} /> : 'No report drafted yet'}
        </span>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );

  const callouts = (
    <>
      {canLead && isDraft && blocker ? <p className="t-meta">{blocker}</p> : null}
      {isDraft && checks.length > 0 ? <ReportChecks checks={checks} /> : null}
      {confirmPublish && isDraft ? (
        <Callout
          tone="ask"
          icon={AlertTriangle}
          actions={
            <>
              <WorkspaceButton variant="tertiary" onClick={() => setConfirmPublish(false)}>
                Cancel
              </WorkspaceButton>
              <WorkspaceButton variant="primary" disabled={busy} onClick={runPublish}>
                {publish.isPending ? <Spinner /> : null}
                Publish
              </WorkspaceButton>
            </>
          }
        >
          Publishing freezes this report, its sections and the figures on its cover as of now, and
          renders one PDF. A published report is never edited; the next one starts as a new draft.
          {checks.length > 0
            ? ` ${checks.length === 1 ? 'One number above is' : `${checks.length} numbers above are`} not from the rollups.`
            : ''}
        </Callout>
      ) : null}
      {notice ? (
        <Callout tone="ok" icon={CheckCircle2} role="status">
          {notice}
        </Callout>
      ) : null}
      {draft.error ? <ErrorMessage error={draft.error} /> : null}
      {publish.error ? <ErrorMessage error={publish.error} /> : null}
    </>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="report-pane">
      {!modules.health_check ? (
        <EmptyState
          title="No report for this engagement"
          description="The Health & readiness report is built on the Health Check module, which is off for this engagement."
        />
      ) : !report ? (
        <>
          {head}
          {callouts}
          <EmptyState
            title="No report yet"
            description={
              canLead
                ? 'Draft with AI writes the health sections and the go-live summary from the confirmed ratings, scores and findings. You edit, then publish once. The figures in it are the rollups, frozen at publish.'
                : 'An engagement lead drafts the report from the confirmed ratings, scores and findings.'
            }
          />
        </>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
          <ReportOutline
            items={report.sections.map((s) => ({
              id: s.section_id,
              title: s.title,
              edited: s.origin === 'consultant_edited',
            }))}
            onJump={jumpTo}
          />
          <div className="flex min-w-0 flex-col gap-4">
            {head}
            {callouts}
            {report.go_live_section === 'missing' ? (
              <Callout tone="note">
                The go-live readiness section is not in this draft. Redraft with AI adds it after
                the health sections.
              </Callout>
            ) : null}
            <article
              aria-label="Health & readiness report"
              className="rounded-card border border-ink-100 bg-paper-0 px-6 py-8 shadow-[0_1px_2px_rgba(24,46,88,0.04)] lg:px-12 lg:py-10"
            >
              <ReportCover
                report={report}
                verdict={verdict}
                figures={figures}
                title={title}
                today={today}
              />
              {report.sections.map((s) => (
                <div
                  key={s.section_id}
                  id={sectionAnchor(s.section_id)}
                  className="flex scroll-mt-20 flex-col gap-2 pt-[22px]"
                >
                  {s.is_safe_refusal && isDraft ? (
                    <Callout tone="warn" icon={AlertTriangle}>
                      The AI could not draft &ldquo;{s.title}&rdquo; from what is confirmed so far.
                      Write it yourself before publishing.
                    </Callout>
                  ) : null}
                  <SectionEditor
                    section={s}
                    title={s.title}
                    description={
                      s.section_key === 'go_live_readiness'
                        ? GO_LIVE_SECTION_DESCRIPTION
                        : undefined
                    }
                    disabled={!isDraft || !canLead || busy}
                    renderText={(text) => <ReportText text={text} values={placeholders} />}
                    editHint={EDIT_HINT}
                    onSave={(input) => edit.mutateAsync({ sectionId: s.section_id, input })}
                  />
                </div>
              ))}
            </article>
            <p className="t-meta">
              One report: the health sections and the go-live summary, one Publish, one PDF. The AI
              writes the words; the numbers and figures are the rollups, never typed by the AI.
            </p>
          </div>
        </div>
      )}

      {pdfOpen && report?.snapshot_id ? (
        <PdfPreviewModal
          engagementId={engagementId}
          snapshotId={report.snapshot_id}
          snapshotLabel={`Health & readiness report · ${report.cycle_label}`}
          open={pdfOpen}
          onOpenChange={setPdfOpen}
        />
      ) : null}
    </div>
  );
}

/**
 * What the read flagged in the draft, before Publish: a percentage typed
 * in the prose, or a placeholder no rollup answers. Advisory; each line
 * jumps to its section.
 */
function ReportChecks({ checks }: { checks: ReportCheck[] }) {
  return (
    <Callout tone="warn" icon={AlertTriangle}>
      <div data-testid="report-checks">
        <p>
          {checks.length === 1
            ? 'One number in the text is not from the rollups.'
            : `${checks.length} numbers in the text are not from the rollups.`}{' '}
          Write a placeholder where a rollup goes &mdash; {'{{readiness_total}}'},{' '}
          {'{{recommendation}}'}, {'{{posture}}'}, {'{{criterion:…}}'}, {'{{domain:…}}'}. You can
          still publish.
        </p>
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {checks.map((c, i) => (
            <li key={`${c.section_id}-${i}`}>
              <button
                type="button"
                onClick={() => jumpTo(c.section_id)}
                className="text-left underline-offset-2 hover:underline"
              >
                <b className="font-semibold text-ink-900">{c.title}</b>:{' '}
                {c.kind === 'typed_percent'
                  ? `“${c.text}” is typed, not a rollup`
                  : `${c.text} matches no domain or criterion`}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Callout>
  );
}

function ReportStatusChip({ status }: { status: HealthReadinessReport['status'] }) {
  if (status === 'draft') return <StatusChip tone="amber">Draft</StatusChip>;
  if (status === 'published') return <StatusChip tone="healthy">Published</StatusChip>;
  return <StatusChip tone="neutral">Publishing</StatusChip>;
}

function ReportMeta({ report }: { report: HealthReadinessReport }) {
  if (report.status === 'published') {
    return (
      <>
        Published {formatDate(report.published_at)} · one PDF · cycle {report.cycle_label}
      </>
    );
  }
  const edited = report.sections.filter((s) => s.origin === 'consultant_edited').length;
  return (
    <>
      Drafted {formatDate(report.drafted_at)} · AI draft
      {edited > 0 ? `, ${edited} section${edited === 1 ? '' : 's'} edited` : ''} · cycle{' '}
      {report.cycle_label}
    </>
  );
}
